import { Injectable, DOCUMENT, inject } from '@angular/core';

import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HelpRestService } from './help-rest.service';
import { Topic, Article, ArticleUrl } from './help.domain';

interface HelpContent { html: string; id: string; position: number }

@Injectable({
  providedIn: 'root'
})
export class HelpService {
  private readonly helpRestService = inject(HelpRestService);
  protected readonly document = inject<Document>(DOCUMENT);

  private static readonly LOAD_TOP_OFFSET: number = 1;
  private static readonly LOAD_BOTTOM_OFFSET: number = 3;

  getTopics(): Observable<Array<Topic>> {
    return this.helpRestService.getIndex();
  }

  getHelpContent(topic: Topic, articleId: string, alreadyLoadedArticles: Array<Article>): Observable<Array<Article>> {
    const articleUrls: Array<ArticleUrl> = [];
    const index = topic.items.findIndex((item: ArticleUrl): boolean => item.id === articleId);
    for (let i = index - HelpService.LOAD_TOP_OFFSET; i <= index + HelpService.LOAD_BOTTOM_OFFSET; i++) {
      const article = topic.items[i];
      if (article !== undefined &&
        !alreadyLoadedArticles.some((item: Article): boolean => item.id === article.id)) {
        articleUrls.push(article);
      }
    }
    return forkJoin(
      articleUrls.map((articleUrl: ArticleUrl): Observable<HelpContent> => this.helpRestService
        .getArticle(articleUrl.url)
        .pipe(
          map((response: string): HelpContent => ({
            id: articleUrl.id,
            position: topic.items.findIndex((item: ArticleUrl): boolean => item.id === articleUrl.id),
            html: response
          })),
          catchError((): Observable<HelpContent> => of({
            id: articleUrl.id,
            position: topic.items.findIndex((item: ArticleUrl): boolean => item.id === articleUrl.id),
            html: '<div></div>'
          }))
        ))
    );
  }

  mergeArticles(alreadyLoadedArticles: Array<Article>, newArticles: Array<Article>): void {
    newArticles.forEach((article: Article): void => {
      if (!alreadyLoadedArticles.some((a: Article): boolean => a.id === article.id)) {
        alreadyLoadedArticles.push(article);
        alreadyLoadedArticles.sort((a: Article, b: Article): number => {
          if (a.position < b.position) {
            return -1;
          }
          if (a.position > b.position) {
            return 1;
          }
          return 0;
        });
      }
    });
  }

  scrollToArticle(articleId: string): void {
    const element = this.document.getElementById(articleId);
    if (element !== undefined && element !== null) {
      element.scrollIntoView(true);
      element.focus();
    }
  }

  scrollToLink(linkId: string): void {
    const element = this.document.getElementById(linkId + '__link');
    element.focus();
  }
}
